import { NavLink, useNavigate, useLocation } from 'react-router-dom';
import { useState, useEffect, useRef } from 'react';
import { getAuth, clearAuth, isSuperAdmin } from '../lib/auth';
import { AccountSwitcher } from './AccountSwitcher';
import { Button } from './ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from './ui/dropdown-menu';
import { LayoutDashboard, FileText, Download, Settings as SettingsIcon, LogOut, Shield, Landmark, MapPin, Box, Gauge, Building, Building2, ChevronDown, CreditCard, Receipt, BarChart3, BadgeDollarSign, ArrowUpDown, Zap, AlertOctagon, Sparkles, Activity, Heart, Mail as MailIcon, ListChecks, ClipboardList, ShieldCheck, MailWarning, FileSignature, ScrollText, Wallet, Boxes, Upload, Rocket, Leaf, Star, LifeBuoy, Fingerprint } from 'lucide-react';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from './ui/tooltip';
import { AiAgentNavLauncher } from './ai_agent/AiAgentNavLauncher';
import NotificationBell from './NotificationBell';
import FreePlanBanner from './FreePlanBanner';
import ExternalMysqlBanner from './ExternalMysqlBanner';
import UpgradeSheet from './subscription/UpgradeSheet';
import { toast } from 'sonner';
import api from '../lib/api';

export const MainLayout = ({ children, wide = false }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = getAuth();
  
  // Use state to track current account so we can update it when AccountSwitcher refreshes data
  const [currentAccount, setCurrentAccountState] = useState(() => {
    const str = localStorage.getItem('current_account');
    return str ? JSON.parse(str) : null;
  });
  
  // Re-check localStorage periodically for account updates from AccountSwitcher
  useEffect(() => {
    const checkAccount = () => {
      const str = localStorage.getItem('current_account');
      const acc = str ? JSON.parse(str) : null;
      if (JSON.stringify(acc) !== JSON.stringify(currentAccount)) {
        setCurrentAccountState(acc);
      }
    };
    
    // Check on mount and every second for 10 seconds (to catch AccountSwitcher updates)
    checkAccount();
    const interval = setInterval(checkAccount, 1000);
    const timeout = setTimeout(() => clearInterval(interval), 10000);
    
    return () => {
      clearInterval(interval);
      clearTimeout(timeout);
    };
  }, [location.pathname]); // Re-run on navigation

  // Lightweight activity heartbeat → records last_visited_page + last_seen_at
  // for the CRM sync. Best-effort, fire-and-forget; a ref guard de-dupes the
  // React StrictMode double-invoke and repeated renders of the same path.
  const lastTrackedPath = useRef(null);
  useEffect(() => {
    if (lastTrackedPath.current === location.pathname) return;
    lastTrackedPath.current = location.pathname;
    api.post('/me/activity', { page: location.pathname }).catch(() => {});
  }, [location.pathname]);

  // Consume a paid-plan signup intent once the user is inside the app: open the
  // Upgrade drawer on the plans tab so they can finish upgrading in one click.
  // Registration stays free — this is a gentle nudge, not a paywall.
  const upgradeIntentConsumed = useRef(false);
  useEffect(() => {
    if (upgradeIntentConsumed.current || !currentAccount?.id) return;
    const intent = localStorage.getItem('bp_upgrade_intent');
    if (!intent || intent === 'free') return;
    upgradeIntentConsumed.current = true;
    localStorage.removeItem('bp_upgrade_intent');
    // No cleanup/clearTimeout here on purpose: under React StrictMode the effect
    // double-invokes and a returned cleanup would cancel the dispatch before it fires.
    setTimeout(() => {
      toast.success(`You're in! Finish setting up ${intent === 'pro' ? 'Pro' : intent} whenever you're ready.`);
      window.dispatchEvent(new CustomEvent('billportal:open-upgrade', { detail: { tab: 'plans', accountId: currentAccount.id } }));
    }, 1200);
  }, [currentAccount?.id]);
  
  // Check if UBM is enabled for current account
  const isUbmEnabled = currentAccount?.enabled_apps?.ubm === true;
  const isUbmRoute = location.pathname.startsWith('/ubm');

  // Onboarding Planner lifecycle: first submenu while active, moves to the
  // bottom once the org marks onboarding Completed.
  const [plannerCompleted, setPlannerCompleted] = useState(false);
  useEffect(() => {
    if (!isUbmEnabled || !currentAccount?.id) { setPlannerCompleted(false); return; }
    let cancelled = false;
    const fetchStatus = () => api.get('/onboarding-planner/plan', { params: { account_id: currentAccount.id } })
      .then(({ data }) => { if (!cancelled) setPlannerCompleted(data?.plan?.status === 'Completed'); })
      .catch(() => {});
    fetchStatus();
    const onChange = (e) => {
      if (e?.detail && typeof e.detail.completed === 'boolean') setPlannerCompleted(e.detail.completed);
      else fetchStatus();
    };
    window.addEventListener('planner-status-changed', onChange);
    return () => { cancelled = true; window.removeEventListener('planner-status-changed', onChange); };
  }, [isUbmEnabled, currentAccount?.id]);
  
  // Check if Payments is enabled for current account
  const isPaymentsEnabled = currentAccount?.enabled_apps?.payments === true;
  const isPaymentsRoute = location.pathname.startsWith('/payments');
  const isBillsRoute = location.pathname.startsWith('/bills');
  // Bill Parsing is a CORE app — Bills navigation should reflect it, not Payments.
  // Default to true so legacy/partial records still see the menu.
  const isBillParsingEnabled = currentAccount?.enabled_apps?.bill_parsing !== false;
  
  // IntakeHub: only visible if enabled_apps.intake_hub is explicitly enabled for the org
  const isIntakeHubEnabled = currentAccount?.enabled_apps?.intake_hub === true;

  // ENERGY STAR (ESPM) reporting — gated by enabled_apps.energy_star
  const isEnergyStarEnabled = currentAccount?.enabled_apps?.energy_star === true;

  // PaaS (Parsing-as-a-Service): additive module, does not affect Intake Hub
  const isPaasEnabled = currentAccount?.enabled_apps?.paas === true;
  const isPaasRoute = location.pathname.startsWith('/paas');

  // Procurement: Contracts module gated by enabled_apps.contracts
  const isContractsEnabled = currentAccount?.enabled_apps?.contracts === true;
  const isBudgetsEnabled = currentAccount?.enabled_apps?.budgets === true;
  const isProcurementRoute = location.pathname.startsWith('/procurement');

  // Sustainability: org-level module gated by enabled_apps.sustainability
  const isSustainabilityEnabled = currentAccount?.enabled_apps?.sustainability === true;

  const handleLogout = () => {
    clearAuth();
    window.location.href = '/login';
  };

  const [supportUnread, setSupportUnread] = useState(0);
  useEffect(() => {
    let alive = true;
    const poll = async () => {
      try {
        const { data } = await api.get('/support/unread-count');
        if (alive) setSupportUnread(data?.count || 0);
      } catch (e) { /* ignore */ }
    };
    poll();
    const t = setInterval(poll, 60000);
    return () => { alive = false; clearInterval(t); };
  }, [location.pathname]);

  const navLinkClass = ({ isActive }) =>
    `flex items-center gap-2 px-4 py-2 text-sm font-medium transition-colors ${
      isActive
        ? 'bg-primary text-primary-foreground'
        : 'text-muted-foreground hover:text-foreground hover:bg-muted'
    }`;

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <header className="border-b border-border bg-card">
        <div className="px-4 md:px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <img src="/billportal-logo.png" alt="BillPortal" className="h-8 w-8" />
            <h1 className="text-2xl font-black tracking-tighter" style={{ fontFamily: 'Chivo, sans-serif' }}>
              BillPortal
            </h1>
          </div>
          <div className="flex items-center gap-4">
            {isSuperAdmin() && (
              <Button
                variant="ghost"
                size="sm"
                className="rounded-none"
                onClick={() => navigate('/super-admin')}
                data-testid="super-admin-link"
              >
                <Shield className="h-4 w-4 mr-2" />
                Super Admin
              </Button>
            )}
            <AccountSwitcher />
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button
                    variant="ghost"
                    size="sm"
                    className="rounded-none relative"
                    onClick={() => navigate('/support')}
                    data-testid="nav-support"
                    aria-label="Contact Support Team by logging support request"
                  >
                    <LifeBuoy className="h-4 w-4" />
                    {supportUnread > 0 && (
                      <span
                        className="absolute -top-0.5 -right-0.5 min-w-[16px] h-4 px-1 rounded-full bg-red-600 text-white text-[10px] font-bold flex items-center justify-center"
                        data-testid="nav-support-badge"
                      >
                        {supportUnread > 9 ? '9+' : supportUnread}
                      </span>
                    )}
                  </Button>
                </TooltipTrigger>
                <TooltipContent data-testid="nav-support-tooltip">Contact Support Team by logging support request</TooltipContent>
              </Tooltip>
            </TooltipProvider>
            <NotificationBell />
            <span
              className="text-sm text-muted-foreground max-w-[10rem] truncate inline-block align-middle"
              title={user?.name}
              data-testid="header-username"
            >
              {user?.name}
            </span>
            <Button
              data-testid="settings-button"
              variant="ghost"
              size="sm"
              className="rounded-none"
              onClick={() => navigate('/settings')}
            >
              <SettingsIcon className="h-4 w-4" />
            </Button>
            <Button
              data-testid="logout-button"
              variant="ghost"
              size="sm"
              className="rounded-none"
              onClick={handleLogout}
            >
              <LogOut className="h-4 w-4" />
            </Button>
          </div>
        </div>
      </header>

      {/* Horizontal Navigation Menu */}
      <nav className="border-b border-border bg-card">
        <div className="px-4 md:px-6">
          <div className="flex items-center justify-between gap-1">
            <div className="flex items-center gap-1">
            <NavLink to="/dashboard" className={navLinkClass} data-testid="nav-dashboard">
              <LayoutDashboard className="h-4 w-4" />
              Dashboard
            </NavLink>
            {isIntakeHubEnabled && (
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <button
                    className={`flex items-center gap-2 px-4 py-2 text-sm font-medium transition-colors rounded-md ${
                      (location.pathname === '/bill-jobs' || location.pathname.startsWith('/intake-hub'))
                        ? 'bg-primary text-primary-foreground'
                        : 'text-muted-foreground hover:text-foreground hover:bg-muted'
                    }`}
                    data-testid="nav-intake-hub-dropdown"
                  >
                    <FileText className="h-4 w-4" />
                    Intake Hub
                    <ChevronDown className="h-3 w-3" />
                  </button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="start" className="w-56">
                  <DropdownMenuItem
                    className="cursor-pointer"
                    onClick={() => navigate('/intake-hub/kpi-dashboard')}
                    data-testid="nav-intake-kpi-dashboard"
                  >
                    <Gauge className="h-4 w-4 mr-2" /> KPI Dashboard
                  </DropdownMenuItem>
                  <DropdownMenuItem
                    className="cursor-pointer"
                    onClick={() => navigate('/bill-jobs')}
                    data-testid="nav-intake-jobs"
                  >
                    <FileText className="h-4 w-4 mr-2" /> Upload &amp; Extract
                  </DropdownMenuItem>
                  <DropdownMenuItem
                    className="cursor-pointer"
                    onClick={() => navigate('/intake-hub/validation-report')}
                    data-testid="nav-intake-validation-report"
                  >
                    <ShieldCheck className="h-4 w-4 mr-2" /> Validation Report
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            )}
            {/* Bulk Downloads hidden — uncomment when needed
            <NavLink to="/bulk-downloads" className={navLinkClass} data-testid="nav-bulk-downloads">
              <Download className="h-4 w-4" />
              Bulk Downloads
            </NavLink>
            */}
            
            {/* Bills - Dropdown. Shown when bill_parsing core app OR intake hub is enabled.
                Dashboard/All Bills require bill_parsing; Missing Bills follows Intake Hub
                (so intake-hub-only orgs keep nav access to it). */}
            {(isBillParsingEnabled || isIntakeHubEnabled || isEnergyStarEnabled) && (
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <button 
                    className={`flex items-center gap-2 px-4 py-2 text-sm font-medium transition-colors ${
                      isBillsRoute
                        ? 'bg-primary text-primary-foreground' 
                        : 'text-muted-foreground hover:text-foreground hover:bg-muted'
                    } rounded-md`}
                    data-testid="nav-bills-dropdown"
                  >
                    <Receipt className="h-4 w-4" />
                    Bills
                    <ChevronDown className="h-3 w-3" />
                  </button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="start" className="w-44">
                  {isBillParsingEnabled && (
                    <>
                      <DropdownMenuItem
                        className="cursor-pointer"
                        onClick={() => navigate('/bills/dashboard')}
                        data-testid="nav-bills-dashboard"
                      >
                        <BarChart3 className="h-4 w-4 mr-2" /> Bills Dashboard
                      </DropdownMenuItem>
                      <DropdownMenuItem
                        className="cursor-pointer"
                        onClick={() => navigate('/bills')}
                        data-testid="nav-bills-list"
                      >
                        <Receipt className="h-4 w-4 mr-2" /> All Bills
                      </DropdownMenuItem>
                      <DropdownMenuItem
                        className="cursor-pointer"
                        onClick={() => navigate('/bills/demand')}
                        data-testid="nav-bills-demand"
                      >
                        <Gauge className="h-4 w-4 mr-2" /> Demand & Power Quality
                      </DropdownMenuItem>
                    </>
                  )}
                  {isIntakeHubEnabled && (
                    <DropdownMenuItem
                      className="cursor-pointer"
                      onClick={() => navigate('/bills/missing')}
                      data-testid="nav-bills-missing"
                    >
                      <MailWarning className="h-4 w-4 mr-2" /> Missing Bills
                    </DropdownMenuItem>
                  )}
                  {isEnergyStarEnabled && (
                    <DropdownMenuItem
                      className="cursor-pointer"
                      onClick={() => navigate('/bills/energy-star')}
                      data-testid="nav-bills-energy-star"
                    >
                      <Star className="h-4 w-4 mr-2" /> Energy Star Reporting
                    </DropdownMenuItem>
                  )}
                </DropdownMenuContent>
              </DropdownMenu>
            )}
            
            {/* PaaS (Parsing-as-a-Service) Dropdown - additive, independent of Intake Hub */}
            {isPaasEnabled && (
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <button
                    className={`flex items-center gap-2 px-4 py-2 text-sm font-medium transition-colors ${
                      isPaasRoute
                        ? 'bg-primary text-primary-foreground'
                        : 'text-muted-foreground hover:text-foreground hover:bg-muted'
                    }`}
                    data-testid="nav-paas"
                  >
                    <Boxes className="h-4 w-4" />
                    PaaS
                    <ChevronDown className="h-3 w-3" />
                  </button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="start" className="w-52 rounded-none">
                  <DropdownMenuItem className="cursor-pointer" onClick={() => navigate('/paas/dashboard')} data-testid="nav-paas-dashboard">
                    <LayoutDashboard className="h-4 w-4 mr-2" /> Dashboard
                  </DropdownMenuItem>
                  <DropdownMenuItem className="cursor-pointer" onClick={() => navigate('/paas/accounts')} data-testid="nav-paas-accounts">
                    <Building2 className="h-4 w-4 mr-2" /> Accounts
                  </DropdownMenuItem>
                  <DropdownMenuItem className="cursor-pointer" onClick={() => navigate('/paas/upload')} data-testid="nav-paas-upload">
                    <Upload className="h-4 w-4 mr-2" /> Upload Bills
                  </DropdownMenuItem>
                  <DropdownMenuItem className="cursor-pointer" onClick={() => navigate('/paas/validations')} data-testid="nav-paas-validations">
                    <ListChecks className="h-4 w-4 mr-2" /> Validations
                  </DropdownMenuItem>
                  <DropdownMenuItem className="cursor-pointer" onClick={() => navigate('/paas/missing')} data-testid="nav-paas-missing">
                    <MailWarning className="h-4 w-4 mr-2" /> Missing Bills
                  </DropdownMenuItem>
                  <DropdownMenuItem className="cursor-pointer" onClick={() => navigate('/paas/demand')} data-testid="nav-paas-demand">
                    <Gauge className="h-4 w-4 mr-2" /> Demand Report
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            )}

            {/* Payments Dropdown - Only show if enabled for org */}
            {isPaymentsEnabled && (
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <button 
                    className={`flex items-center gap-2 px-4 py-2 text-sm font-medium transition-colors ${
                      isPaymentsRoute
                        ? 'bg-primary text-primary-foreground' 
                        : 'text-muted-foreground hover:text-foreground hover:bg-muted'
                    }`}
                    data-testid="nav-payments"
                  >
                    <CreditCard className="h-4 w-4" />
                    Payments
                    <ChevronDown className="h-3 w-3" />
                  </button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="start" className="w-48 rounded-none">
                  <DropdownMenuItem 
                    onClick={() => navigate('/payments/dashboard')}
                    className="cursor-pointer"
                    data-testid="nav-payments-dashboard"
                  >
                    <LayoutDashboard className="h-4 w-4 mr-2" />
                    Dashboard
                  </DropdownMenuItem>
                  <DropdownMenuItem 
                    onClick={() => navigate('/payments')}
                    className="cursor-pointer"
                    data-testid="nav-payments-files"
                  >
                    <FileText className="h-4 w-4 mr-2" />
                    Payment Files
                  </DropdownMenuItem>
                  <DropdownMenuItem 
                    onClick={() => navigate('/payments/bills')}
                    className="cursor-pointer"
                    data-testid="nav-payments-bills"
                  >
                    <CreditCard className="h-4 w-4 mr-2" />
                    PF Bills
                  </DropdownMenuItem>
                  <DropdownMenuItem
                    onClick={() => navigate('/payments/vendor-ach')}
                    className="cursor-pointer"
                    data-testid="nav-payments-vendor-ach"
                  >
                    <ShieldCheck className="h-4 w-4 mr-2" />
                    Vendor ACH
                  </DropdownMenuItem>
                  <DropdownMenuItem 
                    onClick={() => navigate('/payments/service-fees')}
                    className="cursor-pointer"
                    data-testid="nav-payments-service-fees"
                  >
                    <BadgeDollarSign className="h-4 w-4 mr-2" />
                    Service Fee Report
                  </DropdownMenuItem>
                  <DropdownMenuItem 
                    onClick={() => navigate('/payments/variance')}
                    className="cursor-pointer"
                    data-testid="nav-payments-variance"
                  >
                    <ArrowUpDown className="h-4 w-4 mr-2" />
                    Variance Report
                  </DropdownMenuItem>
                  <DropdownMenuItem
                    onClick={() => navigate('/payments/credit-debit')}
                    className="cursor-pointer"
                    data-testid="nav-payments-credit-debit"
                  >
                    <ArrowUpDown className="h-4 w-4 mr-2" />
                    Credit / Debit Report
                  </DropdownMenuItem>
                  <DropdownMenuItem
                    onClick={() => navigate('/payments/refunds-report')}
                    className="cursor-pointer"
                    data-testid="nav-payments-refunds-report"
                  >
                    <ArrowUpDown className="h-4 w-4 mr-2" />
                    Refunds Report
                  </DropdownMenuItem>
                  <DropdownMenuItem
                    onClick={() => navigate('/payments/late-fee-report')}
                    className="cursor-pointer"
                    data-testid="nav-payments-late-fee-report"
                  >
                    <ArrowUpDown className="h-4 w-4 mr-2" />
                    Late Fee Report
                  </DropdownMenuItem>
                  <DropdownMenuItem 
                    onClick={() => navigate('/payments/emergency-bill-upload')}
                    className="cursor-pointer"
                    data-testid="nav-payments-emergency-bill-upload"
                  >
                    <AlertOctagon className="h-4 w-4 mr-2" />
                    Emergency Paid Bill Upload
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            )}

            {/* Procurement Dropdown - Contracts & Budgets (gated by enabled_apps.contracts/budgets) */}
            {(isContractsEnabled || isBudgetsEnabled) && (
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <button
                    className={`flex items-center gap-2 px-4 py-2 text-sm font-medium transition-colors ${
                      isProcurementRoute
                        ? 'bg-primary text-primary-foreground'
                        : 'text-muted-foreground hover:text-foreground hover:bg-muted'
                    }`}
                    data-testid="nav-procurement"
                  >
                    <ScrollText className="h-4 w-4" />
                    Procurement
                    <ChevronDown className="h-3 w-3" />
                  </button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="start" className="w-48">
                  {isContractsEnabled && (
                    <DropdownMenuItem onClick={() => navigate('/procurement/dashboard')} className="cursor-pointer" data-testid="nav-procurement-dashboard">
                      <LayoutDashboard className="h-4 w-4 mr-2" /> Dashboard
                    </DropdownMenuItem>
                  )}
                  {isContractsEnabled && (
                    <DropdownMenuItem onClick={() => navigate('/procurement/contracts')} className="cursor-pointer" data-testid="nav-procurement-contracts">
                      <FileSignature className="h-4 w-4 mr-2" /> Contracts
                    </DropdownMenuItem>
                  )}
                  {isBudgetsEnabled && (
                    <DropdownMenuItem onClick={() => navigate('/procurement/budgets')} className="cursor-pointer" data-testid="nav-procurement-budgets">
                      <Wallet className="h-4 w-4 mr-2" /> Budgets
                    </DropdownMenuItem>
                  )}
                </DropdownMenuContent>
              </DropdownMenu>
            )}

            {isSustainabilityEnabled && (
              <NavLink to="/sustainability/emissions" className={navLinkClass} data-testid="nav-sustainability">
                <Leaf className="h-4 w-4" />
                Sustainability
              </NavLink>
            )}
            {/* UBM Dropdown - shown last (also shown for PaaS orgs) */}
            {(isUbmEnabled || isPaasEnabled) && (
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <button 
                    className={`flex items-center gap-2 px-4 py-2 text-sm font-medium transition-colors ${
                      isUbmRoute 
                        ? 'bg-primary text-primary-foreground' 
                        : 'text-muted-foreground hover:text-foreground hover:bg-muted'
                    }`}
                    data-testid="nav-ubm"
                  >
                    <Landmark className="h-4 w-4" />
                    UBM Onboarding
                    <ChevronDown className="h-3 w-3" />
                  </button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="start" className="w-48 rounded-none">
                  {!plannerCompleted && (
                    <DropdownMenuItem onClick={() => navigate('/ubm/onboarding-planner')} className="cursor-pointer" data-testid="nav-ubm-onboarding-planner">
                      <ClipboardList className="h-4 w-4 mr-2" /> Onboarding Planner
                    </DropdownMenuItem>
                  )}
                  <DropdownMenuItem onClick={() => navigate('/ubm/properties')} className="cursor-pointer">
                    <MapPin className="h-4 w-4 mr-2" /> Properties
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => navigate('/ubm/units')} className="cursor-pointer">
                    <Box className="h-4 w-4 mr-2" /> Units
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => navigate('/ubm/meters')} className="cursor-pointer">
                    <Gauge className="h-4 w-4 mr-2" /> Meters
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => navigate('/ubm/vendors')} className="cursor-pointer">
                    <Building className="h-4 w-4 mr-2" /> Vendor Accounts
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => navigate('/ubm/onboard-entities')} className="cursor-pointer" data-testid="nav-ubm-entities">
                    <Fingerprint className="h-4 w-4 mr-2" /> Identifiers
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => navigate('/ubm/bill2rollin')} className="cursor-pointer" data-testid="nav-ubm-bill2rollin">
                    <Zap className="h-4 w-4 mr-2" /> AI Bill2Rollin
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => navigate('/ubm/migration')} className="cursor-pointer" data-testid="nav-ubm-migration">
                    <Rocket className="h-4 w-4 mr-2" /> AI Migration
                  </DropdownMenuItem>
                  {plannerCompleted && (
                    <DropdownMenuItem onClick={() => navigate('/ubm/onboarding-planner')} className="cursor-pointer" data-testid="nav-ubm-onboarding-planner">
                      <ClipboardList className="h-4 w-4 mr-2" /> Onboarding Planner
                    </DropdownMenuItem>
                  )}
                </DropdownMenuContent>
              </DropdownMenu>
            )}
            </div>
            <div className="flex items-center gap-1.5">
              <AiAgentNavLauncher />
            </div>
          </div>
        </div>
      </nav>

      {/* SuperAdmin-only alert when the external BillPortal MySQL (source of
          synced/demo bill PDFs) is unreachable. */}
      <ExternalMysqlBanner />

      {/* Free-plan welcome / limit banner — suppressed on /bills where the
          context-aware PlanNudgeBanner is the single banner (avoids stacking). */}
      {!location.pathname.startsWith('/bills') && <FreePlanBanner account={currentAccount} />}

      {/* Global one-click upgrade drawer (credit top-up + plan picker) */}
      <UpgradeSheet />

      {/* Page Content */}
      <main className="px-4 md:px-6 py-6">
        {children}
      </main>
    </div>
  );
};

export default MainLayout;
